// src/components/SalesOrderComponent.tsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Customer, InventoryItem, SalesOrder, SalesOrderItem } from '../types/interfaces';
import { MaterialType, Role } from '../types/enums';
import '../App.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import { isZeroCasePackagePrice, resolveProductPackagePrice } from '../utils/salesLinePrice';
import { isTaproomCustomer } from '../utils/taproomCustomer';
import KegTagsPanel from './KegTagsPanel';

interface NewCustomer {
  name: string;
  email: string;
  address?: string;
}

interface PackageType {
  type: string;
  price: string;
  isKegDepositItem: boolean;
  itemName?: string;
}

interface Product {
  id: number;
  name: string;
  packageTypes: PackageType[];
}

interface SiteOption {
  siteId: string;
  name: string;
}

interface SellableItem {
  itemName: string;
  quantity: number;
  unit: string;
  type: string;
}

interface ExtendedSalesOrderItem extends SalesOrderItem {
  kegCodes?: string[];
  /** Set when packaging price could not be resolved — show link to product pricing. */
  priceProductId?: number | null;
  priceMissing?: boolean;
  /** Case package default is $0.00 — visible warning only; does not block save. */
  priceZeroWarning?: boolean;
}

const DEFAULT_SITE_ID = 'BR-AL-20019';

const statusBadgeStyle = (status: string): React.CSSProperties => {
  const s = String(status || 'Draft').toLowerCase();
  if (s === 'approved') {
    return { backgroundColor: '#166534', color: '#fff' };
  }
  if (s === 'cancelled' || s === 'canceled') {
    return { backgroundColor: '#7F1D1D', color: '#fff' };
  }
  // Draft / Saved
  return { backgroundColor: '#92400E', color: '#fff' };
};

const actionBtnBase: React.CSSProperties = {
  padding: '12px 18px',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 600,
  minHeight: '44px',
  flex: '1 1 auto',
};

const SalesOrderComponent: React.FC<{ inventory: InventoryItem[] }> = ({ inventory }) => {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [sites, setSites] = useState<SiteOption[]>([]);
  const [salesOrder, setSalesOrder] = useState<Partial<SalesOrder>>({
    customerId: 0,
    poNumber: '',
    status: 'Draft',
    siteId: DEFAULT_SITE_ID,
  });
  const [items, setItems] = useState<ExtendedSalesOrderItem[]>([
    { itemName: '', quantity: 0, unit: 'Units', hasKegDeposit: false, price: '', kegCodes: [], priceMissing: false, priceProductId: null },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState<NewCustomer>({ name: '', email: '', address: '' });
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [notifying, setNotifying] = useState(false);
  /** Existing Draft opens read-only until Edit; new orders start editable. */
  const [editing, setEditing] = useState(!orderId);
  const [sellableItems, setSellableItems] = useState<SellableItem[]>([]);
  const [sellableMeta, setSellableMeta] = useState<{ locationName?: string; inventorySiteId?: string }>({});
  const [sellableLoading, setSellableLoading] = useState(false);
  const [showKegTags, setShowKegTags] = useState(false);

  const selectedCustomer = customers.find((c) => c.customerId === salesOrder.customerId);
  const taproomOrder = isTaproomCustomer(selectedCustomer);
  const customerType = String(selectedCustomer?.customerType || '').trim();
  const isDistributorCustomer = customerType.toLowerCase() === 'distributor';
  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  })();
  const isSuperAdmin = currentUser?.role === Role.SuperAdmin;

  const orderStatus = String(salesOrder.status || 'Draft');
  const isApproved = orderStatus === 'Approved';
  const isDraft = !isApproved;
  const hasInvoice = !!(salesOrder.invoiceId || salesOrder.invoiceNumber);
  const busy = saving || approving || deleting || creatingInvoice || voiding || notifying;
  const canEditFields = !isApproved && (editing || !orderId);
  const siteChosen = !!String(salesOrder.siteId || '').trim();

  const sellableOptionLabel = (s: SellableItem) =>
    `${s.itemName} - ${Number(s.quantity) || 0} available`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const customerRes = await apiFetch(`${API_BASE_URL}/api/customers`, {
          headers: { Accept: 'application/json' },
        });
        if (!customerRes.ok) {
          throw new Error(`Failed to fetch customers: HTTP ${customerRes.status}`);
        }
        const customerData = await customerRes.json();
        setCustomers(customerData);

        const productRes = await apiFetch(`${API_BASE_URL}/api/products`, {
          headers: { Accept: 'application/json' },
        });
        if (!productRes.ok) {
          throw new Error(`Failed to fetch products: HTTP ${productRes.status}`);
        }
        const productData = await productRes.json();
        setProducts(productData);

        const sitesRes = await apiFetch(`${API_BASE_URL}/api/sites`, {
          headers: { Accept: 'application/json' },
        });
        if (!sitesRes.ok) {
          throw new Error(`Failed to fetch sites: HTTP ${sitesRes.status}`);
        }
        const sitesData = await sitesRes.json();
        setSites(Array.isArray(sitesData) ? sitesData : []);
      } catch (err: any) {
        setError('Failed to load data: ' + err.message);
      }
    };
    fetchData();

    if (orderId) {
      const fetchOrder = async () => {
        try {
          const res = await apiFetch(`${API_BASE_URL}/api/sales-orders/${orderId}`, {
            headers: { Accept: 'application/json' },
          });
          if (!res.ok) {
            throw new Error(`Failed to fetch sales order: HTTP ${res.status}`);
          }
          const data = await res.json();
          setSalesOrder({
            ...data,
            siteId: data.siteId || DEFAULT_SITE_ID,
            invoiceId: data.invoiceId ?? null,
            invoiceNumber: data.invoiceNumber ?? null,
          });
          setItems(
            data.items.map((item: SalesOrderItem) => ({
              ...item,
              price: item.price ?? '',
              kegCodes: item.kegCodes || [],
              priceMissing: false,
              priceProductId: null,
            })) || [{ itemName: '', quantity: 0, unit: 'Units', hasKegDeposit: false, price: '', kegCodes: [], priceMissing: false, priceProductId: null }]
          );
          // Saved orders open in view mode — Edit unlocks fields.
          setEditing(false);
        } catch (err: any) {
          setError('Failed to load sales order: ' + err.message);
        }
      };
      fetchOrder();
    } else {
      setEditing(true);
    }
  }, [orderId]);

  // Cooler on-hand options — same campus cooler as the approve inventory check.
  useEffect(() => {
    const siteId = String(salesOrder.siteId || '').trim();
    let cancelled = false;
    if (!siteId) {
      setSellableItems([]);
      setSellableMeta({});
      setSellableLoading(false);
      return;
    }
    setSellableLoading(true);
    const load = async () => {
      try {
        const res = await apiFetch(
          `${API_BASE_URL}/api/sales-orders/sellable-items?siteId=${encodeURIComponent(siteId)}`,
          { headers: { Accept: 'application/json' } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setSellableItems(Array.isArray(data.items) ? data.items : []);
        setSellableMeta({
          locationName: data.locationName,
          inventorySiteId: data.inventorySiteId,
        });
      } catch {
        if (!cancelled) {
          setSellableItems([]);
          setSellableMeta({});
        }
      } finally {
        if (!cancelled) setSellableLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [salesOrder.siteId]);

  const addItem = () => {
    setItems([...items, { itemName: '', quantity: 0, unit: 'Units', hasKegDeposit: false, price: '', kegCodes: [], priceMissing: false, priceProductId: null }]);
  };

  /**
   * Paying customers are distributors (Gulf Distributing today). Auto-fill unit
   * price from product packaging for those customers only; keep editable.
   * Taproom (internal) customers are always $0.
   */
  const applyAutoPrice = (
    line: ExtendedSalesOrderItem,
    itemName: string,
    opts: { forDistributor?: boolean; forTaproom?: boolean } = {}
  ): ExtendedSalesOrderItem => {
    const forDistributor = opts.forDistributor ?? isDistributorCustomer;
    const forTaproom = opts.forTaproom ?? taproomOrder;
    const resolved = resolveProductPackagePrice(itemName, products);
    if (forTaproom) {
      return {
        ...line,
        itemName,
        price: '0.00',
        hasKegDeposit: resolved.hasKegDeposit,
        unit: 'Units',
        priceMissing: false,
        priceZeroWarning: false,
        priceProductId: resolved.productId,
      };
    }
    if (!forDistributor) {
      return {
        ...line,
        itemName,
        // Preserve a manually entered price when switching items only if set;
        // otherwise leave blank.
        price: line.price && line.itemName === itemName ? line.price : '',
        hasKegDeposit: resolved.hasKegDeposit,
        unit: 'Units',
        priceMissing: !!itemName && !(line.price && line.itemName === itemName),
        priceZeroWarning: false,
        priceProductId: resolved.productId,
      };
    }
    if (resolved.price != null) {
      return {
        ...line,
        itemName,
        price: resolved.price,
        hasKegDeposit: resolved.hasKegDeposit,
        unit: 'Units',
        priceMissing: false,
        priceZeroWarning: isZeroCasePackagePrice(resolved),
        priceProductId: resolved.productId,
      };
    }
    const invItem = inventory.find(
      (i) => i.identifier === itemName && i.type === MaterialType.FinishedGoods
    );
    if (invItem?.price != null && String(invItem.price).trim() !== '' && !Number.isNaN(parseFloat(String(invItem.price)))) {
      return {
        ...line,
        itemName,
        price: parseFloat(String(invItem.price)).toFixed(2),
        hasKegDeposit: invItem.isKegDepositItem === 1,
        unit: 'Units',
        priceMissing: false,
        priceZeroWarning: false,
        priceProductId: resolved.productId,
      };
    }
    return {
      ...line,
      itemName,
      price: '',
      hasKegDeposit: resolved.hasKegDeposit,
      unit: 'Units',
      priceMissing: !!itemName,
      priceZeroWarning: false,
      priceProductId: resolved.productId,
    };
  };

  const handleCustomerChange = (customerId: number) => {
    const next = customers.find((c) => c.customerId === customerId);
    const nextIsTaproom = isTaproomCustomer(next);
    const nextIsDistributor = String(next?.customerType || '').toLowerCase() === 'distributor';
    setSalesOrder({ ...salesOrder, customerId });
    if (nextIsTaproom) {
      setItems((prev) =>
        prev.map((line) =>
          line.itemName
            ? applyAutoPrice(line, line.itemName, { forTaproom: true, forDistributor: false })
            : { ...line, price: '0.00', priceMissing: false }
        )
      );
      return;
    }
    // Recompute packaging prices when switching to a distributor (e.g. Gulf).
    if (nextIsDistributor) {
      setItems((prev) =>
        prev.map((line) =>
          line.itemName
            ? applyAutoPrice(line, line.itemName, { forDistributor: true, forTaproom: false })
            : line
        )
      );
    }
  };

  const updateItem = (index: number, field: keyof ExtendedSalesOrderItem, value: any) => {
    const updatedItems = [...items];
    if (field === 'itemName' && typeof value === 'string') {
      updatedItems[index] = applyAutoPrice({ ...updatedItems[index] }, value);
    } else if (field === 'price' && typeof value === 'string') {
      updatedItems[index] = {
        ...updatedItems[index],
        price: taproomOrder ? '0.00' : value,
        priceMissing: taproomOrder ? false : value.trim() === '',
        // Manual edit acknowledges a $0 package default; clear the soft warning.
        priceZeroWarning: false,
      };
    } else if (field === 'hasKegDeposit' && typeof value === 'boolean') {
      updatedItems[index] = { ...updatedItems[index], hasKegDeposit: value };
    } else {
      updatedItems[index] = { ...updatedItems[index], [field]: value };
    }
    setItems(updatedItems);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!salesOrder.customerId) {
      setError('Customer is required');
      return;
    }
    if (
      items.some(
        (item) =>
          !item.itemName ||
          item.quantity <= 0 ||
          !item.unit ||
          item.price === '' ||
          item.price == null ||
          isNaN(parseFloat(item.price)) ||
          parseFloat(item.price) < 0
      )
    ) {
      const missing = items.find((item) => item.itemName && (item.priceMissing || item.price === '' || item.price == null));
      if (missing?.priceProductId) {
        setError(
          `Price is required for "${missing.itemName}". Set packaging price on the product, or enter a price here.`
        );
      } else {
        setError('Customer and valid items with prices are required');
      }
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const method = orderId ? 'PATCH' : 'POST';
      const url = orderId ? `${API_BASE_URL}/api/sales-orders/${orderId}` : `${API_BASE_URL}/api/sales-orders`;
      // Never send status — Save must not approve or create an invoice.
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          customerId: salesOrder.customerId,
          poNumber: salesOrder.poNumber || null,
          siteId: salesOrder.siteId || DEFAULT_SITE_ID,
          items: items.map((item) => ({
            itemName: item.itemName,
            quantity: item.quantity,
            unit: item.unit,
            price: item.price,
            hasKegDeposit: item.hasKegDeposit ? 1 : 0,
            // Keg serials are collected at invoice/ship, not on the sales order.
            kegCodes: null,
          })),
        }),
      });
      if (!res.ok) {
        let message = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          message = body.error || message;
        } catch {
          message = await res.text();
        }
        throw new Error(message);
      }
      const data = await res.json();
      setSalesOrder({
        ...data,
        siteId: data.siteId || salesOrder.siteId || DEFAULT_SITE_ID,
        invoiceId: data.invoiceId ?? salesOrder.invoiceId ?? null,
        invoiceNumber: data.invoiceNumber ?? salesOrder.invoiceNumber ?? null,
      });
      setItems(
        data.items.map((item: SalesOrderItem) => ({
          ...item,
          price: item.price ?? '',
          hasKegDeposit: !!item.hasKegDeposit,
          kegCodes: item.kegCodes || [],
          priceMissing: false,
          priceProductId: null,
        })) || [{ itemName: '', quantity: 0, unit: 'Units', hasKegDeposit: false, price: '', kegCodes: [], priceMissing: false, priceProductId: null }]
      );
      setEditing(false);
      setSuccessMessage(`Sales order ${orderId ? 'updated' : 'created'} successfully`);
      setTimeout(() => {
        setSuccessMessage(null);
        navigate(`/sales-orders/${data.orderId || orderId}`);
      }, 2000);
      setError(null);
    } catch (err: any) {
      setError('Failed to save sales order: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDraft = async () => {
    if (!salesOrder.orderId || salesOrder.status !== 'Draft') return;
    const ok = window.confirm(
      `Delete draft sales order #${salesOrder.orderId}? This cannot be undone.`
    );
    if (!ok) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/sales-orders/${salesOrder.orderId}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      navigate('/sales-orders');
    } catch (err: any) {
      const msg = String(err?.message || 'Could not delete the sales order.');
      // Prefer the server's plain message; never surface raw SQL to the user.
      setError(
        /foreign key|violates|SQLITE_|constraint/i.test(msg)
          ? 'This sales order still has a linked invoice and cannot be deleted.'
          : msg
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleApprove = async () => {
    if (!salesOrder.orderId || !salesOrder.customerId || !items.length) {
      setError('Cannot approve: Missing order ID, customer, or items');
      return;
    }
    setApproving(true);
    setError(null);
    try {
      // Explicit approve endpoint — the only path that creates an invoice.
      const res = await apiFetch(
        `${API_BASE_URL}/api/sales-orders/${salesOrder.orderId}/approve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            customerId: salesOrder.customerId,
            poNumber: salesOrder.poNumber || null,
            siteId: salesOrder.siteId || DEFAULT_SITE_ID,
            items: items.map((item) => ({
              itemName: item.itemName,
              quantity: item.quantity,
              unit: item.unit,
              price: item.price,
              hasKegDeposit: item.hasKegDeposit ? 1 : 0,
              kegCodes: null,
            })),
          }),
        }
      );
      if (!res.ok) {
        let message = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          message = body.error || message;
        } catch {
          message = await res.text();
        }
        throw new Error(message);
      }
      const data = await res.json();
      setSalesOrder({
        ...data,
        siteId: data.siteId || salesOrder.siteId || DEFAULT_SITE_ID,
        invoiceId: data.invoiceId ?? null,
        invoiceNumber: data.invoiceNumber ?? null,
      });
      setEditing(false);
      const invLabel = data.invoiceNumber || data.invoiceId;
      setSuccessMessage(
        data.invoiceId
          ? `Sales order approved, invoice ${invLabel} created`
          : 'Sales order approved'
      );
      setTimeout(() => {
        setSuccessMessage(null);
        if (data.invoiceId) navigate(`/invoices/${data.invoiceId}`);
      }, 1500);
      setError(null);
    } catch (err: any) {
      setError('Failed to approve sales order: ' + err.message);
    } finally {
      setApproving(false);
    }
  };

  const handleVoidInvoiceRevertDraft = async () => {
    if (!salesOrder.orderId || !isSuperAdmin) return;
    const ok = window.confirm(
      `Void invoice${salesOrder.invoiceNumber ? ` ${salesOrder.invoiceNumber}` : ''} for sales order #${salesOrder.orderId} and revert the order to Draft?\n\n` +
        `This does not delete data. If the invoice was posted, stock is restored to the brewery cooler.`
    );
    if (!ok) return;
    setVoiding(true);
    setError(null);
    try {
      const res = await apiFetch(
        `${API_BASE_URL}/api/admin/sales-orders/${salesOrder.orderId}/void-invoice-revert-draft`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ confirm: 'VOID_AND_REVERT' }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setSalesOrder({
        ...salesOrder,
        status: 'Draft',
        invoiceId: undefined,
        invoiceNumber: undefined,
      });
      setEditing(false);
      setSuccessMessage(
        data.invoiceVoided
          ? `Invoice voided; sales order #${salesOrder.orderId} is Draft again`
          : `Sales order #${salesOrder.orderId} set back to Draft`
      );
      setTimeout(() => setSuccessMessage(null), 2500);
    } catch (err: any) {
      setError('Failed to void / revert: ' + err.message);
    } finally {
      setVoiding(false);
    }
  };

  /** Recovery: Approved SO with no invoice yet (prior create failed). */
  const handleCreateInvoice = async () => {
    if (!salesOrder.orderId || !isApproved) return;
    setCreatingInvoice(true);
    setError(null);
    try {
      const res = await apiFetch(
        `${API_BASE_URL}/api/sales-orders/${salesOrder.orderId}/create-invoice`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            customerId: salesOrder.customerId,
            poNumber: salesOrder.poNumber || null,
            siteId: salesOrder.siteId || DEFAULT_SITE_ID,
            items: items.map((item) => ({
              itemName: item.itemName,
              quantity: item.quantity,
              unit: item.unit,
              price: item.price,
              hasKegDeposit: item.hasKegDeposit ? 1 : 0,
              kegCodes: null,
            })),
          }),
        }
      );
      if (!res.ok) {
        let message = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          message = body.error || message;
        } catch {
          message = await res.text();
        }
        throw new Error(message);
      }
      const data = await res.json();
      setSalesOrder({
        ...salesOrder,
        ...data,
        siteId: data.siteId || salesOrder.siteId || DEFAULT_SITE_ID,
        invoiceId: data.invoiceId ?? null,
        invoiceNumber: data.invoiceNumber ?? null,
      });
      const invLabel = data.invoiceNumber || data.invoiceId;
      setSuccessMessage(`Invoice ${invLabel} created`);
      setTimeout(() => {
        setSuccessMessage(null);
        if (data.invoiceId) navigate(`/invoices/${data.invoiceId}`);
      }, 1500);
    } catch (err: any) {
      setError('Failed to create invoice: ' + err.message);
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleAddCustomer = async () => {
    if (!newCustomer.name || !newCustomer.email) {
      setError('Customer name and email are required');
      return;
    }
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...newCustomer, enabled: 1 }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to add customer: ${text}`);
      }
      const data = await res.json();
      setCustomers([...customers, data]);
      setNewCustomer({ name: '', email: '', address: '' });
      setShowAddCustomerModal(false);
      setError(null);
    } catch (err: any) {
      setError('Failed to add customer: ' + err.message);
    }
  };

  const handleResendAssociateEmail = async () => {
    if (!orderId || notifying) return;
    setNotifying(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/sales-orders/${orderId}/notify`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });
      let data: { ok?: boolean; to?: string; error?: string; message?: string } = {};
      try {
        data = await res.json();
      } catch {
        /* non-JSON */
      }
      if (!res.ok || !data.ok) {
        setError(data.error || data.message || `Failed to send email (HTTP ${res.status})`);
        return;
      }
      setSuccessMessage(data.message || `Email sent to ${data.to}`);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setError('Failed to send email: ' + (err?.message || 'Unknown error'));
    } finally {
      setNotifying(false);
    }
  };

  return (
    <div className="page-container">
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px',
        }}
      >
        <h2 style={{ color: '#EEC930', fontSize: '24px', margin: 0 }}>
          {orderId ? `Sales Order ${orderId}` : 'Create Sales Order'}
        </h2>
        {orderId && (
          <span
            data-testid="so-status-badge"
            style={{
              ...statusBadgeStyle(orderStatus),
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
            }}
          >
            {orderStatus || 'Draft'}
          </span>
        )}
        {isApproved && hasInvoice && salesOrder.invoiceNumber && (
          <span style={{ color: '#555', fontSize: '14px' }}>
            Invoice {salesOrder.invoiceNumber}
          </span>
        )}
      </div>
      {error && <div className="error">{error}</div>}
      {successMessage && (
        <div
          style={{
            color: '#28A745',
            backgroundColor: '#e6ffe6',
            padding: '10px',
            borderRadius: '4px',
            marginBottom: '10px',
            textAlign: 'center',
          }}
        >
          {successMessage}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px', maxWidth: '600px' }}>
        <div>
          <label style={{ fontWeight: 'bold', color: '#EEC930', display: 'block', marginBottom: '5px' }}>
            Customer (required):
          </label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select
              data-testid="so-customer"
              value={salesOrder.customerId || ''}
              onChange={(e) => handleCustomerChange(parseInt(e.target.value) || 0)}
              style={{ width: '100%', padding: '10px', border: '1px solid #CCCCCC', borderRadius: '4px', fontSize: '16px' }}
              disabled={!canEditFields}
            >
              <option value="">Select Customer</option>
              {customers.map((customer) => (
                <option key={customer.customerId} value={customer.customerId}>
                  {customer.name}
                </option>
              ))}
            </select>
            {canEditFields && (
              <button
                onClick={() => setShowAddCustomerModal(true)}
                style={{
                  backgroundColor: '#2196F3',
                  color: '#fff',
                  padding: '10px 15px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  minHeight: '44px',
                }}
              >
                Add Customer
              </button>
            )}
          </div>
          {taproomOrder && (
            <p style={{ color: '#555', fontSize: '13px', marginTop: '8px' }}>
              Internal taproom order: lines are $0 / Tax Free. Posting the invoice moves stock from the
              brewery cooler to the taproom cooler (TRANSFER + TO number; keg serials required for kegs).
            </p>
          )}
        </div>
        <div>
          <label style={{ fontWeight: 'bold', color: '#EEC930', display: 'block', marginBottom: '5px' }}>
            PO Number (optional):
          </label>
          <input
            type="text"
            value={salesOrder.poNumber || ''}
            onChange={(e) => setSalesOrder({ ...salesOrder, poNumber: e.target.value })}
            placeholder="Enter PO Number"
            style={{ width: '100%', padding: '10px', border: '1px solid #CCCCCC', borderRadius: '4px', fontSize: '16px' }}
            disabled={!canEditFields}
          />
        </div>
        <div>
          <label
            htmlFor="so-site-select"
            style={{ fontWeight: 'bold', color: '#EEC930', display: 'block', marginBottom: '5px' }}
          >
            Selling location (invoice prefix):
          </label>
          <select
            id="so-site-select"
            data-testid="so-site-select"
            value={salesOrder.siteId || ''}
            onChange={(e) => setSalesOrder({ ...salesOrder, siteId: e.target.value })}
            style={{ width: '100%', padding: '10px', border: '1px solid #CCCCCC', borderRadius: '4px', fontSize: '16px' }}
            disabled={!canEditFields || busy}
          >
            <option value="">Select selling location</option>
            {sites.length === 0 && <option value={DEFAULT_SITE_ID}>Madison Brewery (E-)</option>}
            {sites.map((site) => {
              const madison = ['BR-AL-20019', 'DSP-AL-20010', 'TAPROOM-MADISON'].includes(site.siteId);
              const athens = ['BR-AL-20088', 'DSP-AL-20051', 'TAPROOM-ATHENS'].includes(site.siteId);
              const prefix = madison ? 'E-' : athens ? 'ATH-' : '?-';
              return (
                <option key={site.siteId} value={site.siteId}>
                  {site.name} ({site.siteId}) → {prefix}
                </option>
              );
            })}
          </select>
        </div>
        <div>
          <label style={{ fontWeight: 'bold', color: '#EEC930', display: 'block', marginBottom: '5px' }}>
            Items (required):
          </label>
          <p style={{ color: '#666', fontSize: '13px', margin: '0 0 10px' }} data-testid="so-cooler-hint">
            {!siteChosen
              ? 'Pick a selling location first to load cooler stock.'
              : `Items on hand in ${sellableMeta.locationName || 'the brewery cooler'}${
                  sellableMeta.inventorySiteId ? ` (${sellableMeta.inventorySiteId})` : ''
                }.`}
          </p>

          {items.map((item, index) => {
            const inList = sellableItems.some((s) => s.itemName === item.itemName);
            return (
              <div key={index} style={{ marginBottom: '16px' }} data-testid={`so-line-${index}`}>
                <div
                  style={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <select
                    value={item.itemName}
                    onChange={(e) => updateItem(index, 'itemName', e.target.value)}
                    data-testid={`so-item-select-${index}`}
                    aria-label={`Sales order line ${index + 1} item`}
                    style={{
                      flex: '1 1 260px',
                      minWidth: '220px',
                      maxWidth: '100%',
                      padding: '10px',
                      border: '1px solid #CCCCCC',
                      borderRadius: '4px',
                      fontSize: '16px',
                      minHeight: '44px',
                      backgroundColor: '#FFFFFF',
                      color: '#222222',
                    }}
                    disabled={!canEditFields || busy || !siteChosen || sellableLoading}
                  >
                    <option value="">
                      {!siteChosen
                        ? 'Select selling location first'
                        : sellableLoading
                          ? 'Loading cooler stock…'
                          : sellableItems.length === 0
                            ? 'No cooler stock available'
                            : 'Select item'}
                    </option>
                    {item.itemName && !inList && (
                      <option value={item.itemName}>{item.itemName}</option>
                    )}
                    {sellableItems.map((s) => (
                      <option key={s.itemName} value={s.itemName}>
                        {sellableOptionLabel(s)}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={item.quantity || ''}
                    onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                    placeholder="Quantity"
                    min="0"
                    data-testid={`so-qty-${index}`}
                    style={{
                      width: '100px',
                      padding: '10px',
                      border: '1px solid #CCCCCC',
                      borderRadius: '4px',
                      fontSize: '16px',
                      minHeight: '44px',
                    }}
                    disabled={!canEditFields || busy}
                    aria-label={`Sales order line ${index + 1} quantity`}
                  />
                  <select
                    value={item.unit}
                    onChange={(e) => updateItem(index, 'unit', e.target.value)}
                    style={{
                      width: '100px',
                      padding: '10px',
                      border: '1px solid #CCCCCC',
                      borderRadius: '4px',
                      fontSize: '16px',
                      minHeight: '44px',
                    }}
                    disabled={!canEditFields || busy}
                  >
                    <option value="Units">Units</option>
                    <option value="Kegs">Kegs</option>
                    <option value="Bottles">Bottles</option>
                    <option value="Cans">Cans</option>
                  </select>
                  <input
                    type="number"
                    value={item.price === '' || item.price == null ? '' : item.price}
                    onChange={(e) => updateItem(index, 'price', e.target.value)}
                    placeholder="Price"
                    step="0.01"
                    min="0"
                    data-testid={`so-price-${index}`}
                    style={{
                      width: '100px',
                      padding: '10px',
                      border: item.priceMissing ? '1px solid #F86752' : '1px solid #CCCCCC',
                      borderRadius: '4px',
                      fontSize: '16px',
                      minHeight: '44px',
                    }}
                    disabled={taproomOrder || !canEditFields || busy}
                    title={taproomOrder ? 'Taproom invoices are $0' : undefined}
                  />
                  <label title="Keg deposit flag only — serials are scanned at invoice/ship">
                    <input
                      type="checkbox"
                      checked={item.hasKegDeposit}
                      onChange={(e) => updateItem(index, 'hasKegDeposit', e.target.checked)}
                      disabled={!canEditFields || busy}
                    />
                    Keg Deposit
                  </label>
                  {canEditFields && (
                    <button
                      onClick={() => removeItem(index)}
                      style={{
                        backgroundColor: '#F86752',
                        color: '#fff',
                        padding: '8px 12px',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        minHeight: '44px',
                      }}
                      disabled={busy}
                    >
                      Remove
                    </button>
                  )}
                </div>
                {!taproomOrder && item.priceMissing && item.itemName && (
                  <p style={{ color: '#B45309', fontSize: '13px', margin: '6px 0 0' }}>
                    {isDistributorCustomer ? (
                      <>
                        No packaging price found for this distributor line.
                        {item.priceProductId ? (
                          <>
                            {' '}
                            <Link to={`/products/${item.priceProductId}`}>Open product pricing</Link>
                            {' '}to set it, or type a price here.
                          </>
                        ) : (
                          <> Enter a price, or add the SKU under Product → Package Types.</>
                        )}
                      </>
                    ) : (
                      <>Enter a unit price for this line (packaging auto-fill applies to distributor customers).</>
                    )}
                  </p>
                )}
                {!taproomOrder && item.priceZeroWarning && item.itemName && (
                  <p
                    data-testid="so-zero-case-price-warning"
                    style={{ color: '#B45309', fontSize: '13px', margin: '6px 0 0' }}
                  >
                    This case package defaults to $0.00.
                    {item.priceProductId ? (
                      <>
                        {' '}
                        <Link to={`/products/${item.priceProductId}`}>Open product pricing</Link>
                        {' '}to update it, or type a price here.
                      </>
                    ) : (
                      <> Type a price here if that is not intentional.</>
                    )}
                  </p>
                )}
              </div>
            );
          })}

          <p style={{ color: '#666', fontSize: '13px', marginBottom: '8px' }}>
            Keg serials are scanned at packaging and when posting the invoice — not required to approve the sales order.
          </p>
          {canEditFields && (
            <button
              onClick={addItem}
              style={{
                backgroundColor: '#2196F3',
                color: '#fff',
                padding: '8px 12px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                minHeight: '44px',
              }}
              disabled={busy}
            >
              Add Item
            </button>
          )}
        </div>
      </div>
      <div
        data-testid="so-actions"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          marginTop: '20px',
          maxWidth: '720px',
        }}
      >
        {/* New order (not yet saved) */}
        {!orderId && (
          <>
            <button
              type="button"
              onClick={handleSave}
              data-testid="so-save"
              style={{
                ...actionBtnBase,
                backgroundColor: '#2196F3',
                color: '#fff',
                cursor: busy ? 'wait' : 'pointer',
                opacity: busy ? 0.7 : 1,
              }}
              disabled={busy}
            >
              {saving ? 'Saving…' : 'Save Sales Order'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/sales-orders')}
              style={{
                ...actionBtnBase,
                backgroundColor: '#F86752',
                color: '#fff',
              }}
              disabled={busy}
            >
              Cancel
            </button>
          </>
        )}

        {/* Draft / Saved: Edit, Approve & Create Invoice, Delete Draft */}
        {orderId && isDraft && (
          <>
            {!editing ? (
              <button
                type="button"
                onClick={() => setEditing(true)}
                data-testid="so-edit"
                style={{
                  ...actionBtnBase,
                  backgroundColor: '#2196F3',
                  color: '#fff',
                }}
                disabled={busy}
              >
                Edit
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSave}
                data-testid="so-save"
                style={{
                  ...actionBtnBase,
                  backgroundColor: '#2196F3',
                  color: '#fff',
                  cursor: busy ? 'wait' : 'pointer',
                  opacity: busy ? 0.7 : 1,
                }}
                disabled={busy}
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            )}
            <button
              type="button"
              onClick={handleApprove}
              data-testid="so-approve"
              style={{
                ...actionBtnBase,
                backgroundColor: '#28A745',
                color: '#fff',
                cursor: busy ? 'wait' : 'pointer',
                opacity: busy ? 0.7 : 1,
              }}
              disabled={busy}
            >
              {approving ? 'Approving…' : 'Approve & Create Invoice'}
            </button>
            <button
              type="button"
              onClick={handleDeleteDraft}
              data-testid="so-delete-draft"
              style={{
                ...actionBtnBase,
                backgroundColor: '#fff',
                color: '#B91C1C',
                border: '2px solid #B91C1C',
                cursor: busy ? 'wait' : 'pointer',
                opacity: busy ? 0.7 : 1,
              }}
              disabled={busy}
              aria-label={`Delete draft sales order ${salesOrder.orderId}`}
            >
              {deleting ? 'Deleting…' : 'Delete Draft'}
            </button>
            <button
              type="button"
              onClick={() => {
                if (editing && salesOrder.orderId) {
                  setEditing(false);
                  setError(null);
                } else {
                  navigate('/sales-orders');
                }
              }}
              style={{
                ...actionBtnBase,
                backgroundColor: '#6B7280',
                color: '#fff',
              }}
              disabled={busy}
            >
              {editing ? 'Cancel Edit' : 'Back'}
            </button>
          </>
        )}

        {/* Approved with invoice: View Invoice + Print BOL */}
        {orderId && isApproved && hasInvoice && (
          <>
            <Link
              to={`/invoices/${salesOrder.invoiceId}`}
              data-testid="so-view-invoice"
              style={{
                ...actionBtnBase,
                backgroundColor: '#2196F3',
                color: '#fff',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              View Invoice
              {salesOrder.invoiceNumber ? ` ${salesOrder.invoiceNumber}` : ''}
            </Link>
            <Link
              to={`/invoices/${salesOrder.invoiceId}/bol`}
              data-testid="so-print-bol"
              style={{
                ...actionBtnBase,
                backgroundColor: '#111827',
                color: '#fff',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              Print BOL
            </Link>
            {isSuperAdmin && (
              <button
                type="button"
                onClick={handleVoidInvoiceRevertDraft}
                data-testid="so-void-revert"
                style={{
                  ...actionBtnBase,
                  backgroundColor: '#fff',
                  color: '#92400E',
                  border: '2px solid #92400E',
                  cursor: busy ? 'wait' : 'pointer',
                  opacity: busy ? 0.7 : 1,
                }}
                disabled={busy}
              >
                {voiding ? 'Reverting…' : 'Void Invoice & Revert to Draft'}
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/sales-orders')}
              style={{
                ...actionBtnBase,
                backgroundColor: '#6B7280',
                color: '#fff',
              }}
            >
              Back
            </button>
          </>
        )}

        {/* Approved but invoice create failed earlier — recovery */}
        {orderId && isApproved && !hasInvoice && (
          <>
            <button
              type="button"
              onClick={handleCreateInvoice}
              data-testid="so-create-invoice"
              style={{
                ...actionBtnBase,
                backgroundColor: '#28A745',
                color: '#fff',
                cursor: busy ? 'wait' : 'pointer',
                opacity: busy ? 0.7 : 1,
              }}
              disabled={busy}
            >
              {creatingInvoice ? 'Creating Invoice…' : 'Create Invoice'}
            </button>
            {isSuperAdmin && (
              <button
                type="button"
                onClick={handleVoidInvoiceRevertDraft}
                data-testid="so-void-revert"
                style={{
                  ...actionBtnBase,
                  backgroundColor: '#fff',
                  color: '#92400E',
                  border: '2px solid #92400E',
                  cursor: busy ? 'wait' : 'pointer',
                  opacity: busy ? 0.7 : 1,
                }}
                disabled={busy}
              >
                {voiding ? 'Reverting…' : 'Revert to Draft'}
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/sales-orders')}
              style={{
                ...actionBtnBase,
                backgroundColor: '#6B7280',
                color: '#fff',
              }}
              disabled={busy}
            >
              Back
            </button>
          </>
        )}

        {/* Re-send sales-associate notification (any saved SO; all roles) */}
        {orderId && (
          <button
            type="button"
            onClick={handleResendAssociateEmail}
            data-testid="so-resend-email"
            style={{
              ...actionBtnBase,
              backgroundColor: '#0F766E',
              color: '#fff',
              cursor: busy ? 'wait' : 'pointer',
              opacity: busy ? 0.7 : 1,
            }}
            disabled={busy}
            aria-label="Resend sales associate email"
          >
            {notifying ? 'Sending…' : 'Resend Email'}
          </button>
        )}
        {orderId && (
          <button
            type="button"
            onClick={() => setShowKegTags(true)}
            data-testid="so-keg-tags"
            style={{
              ...actionBtnBase,
              backgroundColor: '#374151',
              color: '#fff',
              cursor: 'pointer',
            }}
            aria-label="View keg tags for this sales order"
          >
            Keg Tags
          </button>
        )}
      </div>
      {showAddCustomerModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000,
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '8px',
              width: '400px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            }}
          >
            <h3 style={{ color: '#555', marginBottom: '20px', textAlign: 'center' }}>
              Add New Customer
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px' }}>
              <div>
                <label style={{ fontWeight: 'bold', color: '#555', display: 'block', marginBottom: '5px' }}>
                  Name (required):
                </label>
                <input
                  type="text"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  placeholder="Enter customer name"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #CCCCCC',
                    borderRadius: '4px',
                    fontSize: '16px',
                  }}
                />
              </div>
              <div>
                <label style={{ fontWeight: 'bold', color: '#555', display: 'block', marginBottom: '5px' }}>
                  Email (required):
                </label>
                <input
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                  placeholder="Enter customer email"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #CCCCCC',
                    borderRadius: '4px',
                    fontSize: '16px',
                  }}
                />
              </div>
              <div>
                <label style={{ fontWeight: 'bold', color: '#555', display: 'block', marginBottom: '5px' }}>
                  Address (optional):
                </label>
                <input
                  type="text"
                  value={newCustomer.address || ''}
                  onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                  placeholder="Enter customer address"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #CCCCCC',
                    borderRadius: '4px',
                    fontSize: '16px',
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
              <button
                onClick={handleAddCustomer}
                style={{
                  backgroundColor: '#2196F3',
                  color: '#fff',
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Create
              </button>
              <button
                onClick={() => {
                  setShowAddCustomerModal(false);
                  setNewCustomer({ name: '', email: '', address: '' });
                  setError(null);
                }}
                style={{
                  backgroundColor: '#F86752',
                  color: '#fff',
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showKegTags && orderId && (
        <KegTagsPanel
          apiPath={`sales-orders/${orderId}/keg-tags`}
          parentLabel="Sales Orders"
          parentTo="/sales-orders"
          documentLabel={`Sales Order ${orderId}`}
          onClose={() => setShowKegTags(false)}
        />
      )}
    </div>
  );
};

export default SalesOrderComponent;